// ABOUTME: React Query mutation hooks for creating, updating, and deleting operational hours configurations.
// ABOUTME: Transforms form values to API request shape and invalidates queries on success.

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../../../../App.store'
import { DAY_ID_TO_CODE, DayIdentifier } from '../types/deliveryWindow'
import {
  OperationalHoursApiResponse,
  OperationalHoursFormValues,
  OperationalHoursOverrides,
} from '../types/operationalHours'
import { toCreateScopeAndConfigRequest } from '../utils/pendingScope'
import type { PendingScopeRequest } from '../context/EntityFormContext'

/** Appends ":00" to display time "HH:mm" → API time "HH:mm:ss" */
const toApiTime = (displayTime: string): string =>
  displayTime ? `${displayTime}:00` : ''

/** Converts form values to the API request body */
const toApiRequest = (values: OperationalHoursFormValues, overrides: OperationalHoursOverrides) => ({
  timeZone: {
    isOverridden: overrides.timeZone,
    value: values.timeZone,
  },
  businessDays: {
    isOverridden: overrides.businessDays,
    value: { activeDays: values.businessDays.map((d) => DAY_ID_TO_CODE[d]) },
  },
  operatingSchedule: {
    isOverridden: overrides.operatingSchedule,
    value: {
      standard: {
        standardDays: values.standardDays.map((d) => DAY_ID_TO_CODE[d]),
        opens: toApiTime(values.standardHours.opens),
        prePlanning: toApiTime(values.standardHours.prePlanning),
        closes: toApiTime(values.standardHours.closes),
      },
      nonStandard: Object.entries(values.nonStandardHours)
        .filter(([dayId]) =>
          values.businessDays.includes(dayId as DayIdentifier) && !values.standardDays.includes(dayId as DayIdentifier))
        .map(([dayId, hours]) => ({
        day: DAY_ID_TO_CODE[dayId as keyof typeof DAY_ID_TO_CODE],
        opens: toApiTime(hours.opens),
        prePlanning: toApiTime(hours.prePlanning),
        closes: toApiTime(hours.closes),
      })),
    },
  },
  nonWorkingDays: {
    isOverridden: overrides.nonWorkingDays,
    value: values.nonWorkingDays,
  },
})

export const useCreateOperationalHours = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values, overrides }: { scopeId: string; values: OperationalHoursFormValues; overrides: OperationalHoursOverrides }) => {
      const res = await api.post<OperationalHoursApiResponse>(
        `/tuc-configs/operational-hours/${scopeId}`,
        toApiRequest(values, overrides)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operationalHoursConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'operationalHours', countryId, businessLine] })
    },
  })
}

export const useCreateOperationalHoursWithScope = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scope, values, overrides }: { scope: PendingScopeRequest; values: OperationalHoursFormValues; overrides: OperationalHoursOverrides }) => {
      const res = await api.post<OperationalHoursApiResponse>(
        '/tuc-configs/operational-hours',
        toCreateScopeAndConfigRequest(scope, toApiRequest(values, overrides))
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operationalHoursConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'operationalHours', countryId, businessLine] })
    },
  })
}

export const useUpdateOperationalHours = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values, overrides }: { scopeId: string; values: OperationalHoursFormValues; overrides: OperationalHoursOverrides }) => {
      const res = await api.put<OperationalHoursApiResponse>(
        `/tuc-configs/operational-hours/${scopeId}`,
        toApiRequest(values, overrides)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operationalHoursConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'operationalHours', countryId, businessLine] })
    },
  })
}

export const useDeleteOperationalHours = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (scopeId: string) => {
      await api.delete(`/tuc-configs/operational-hours/${scopeId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operationalHoursConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'operationalHours', countryId, businessLine] })
    },
  })
}
